/**
 * Created by mongoose on 8/11/17.
 */
import { BASE_URL } from "../../request";
import CONSTANTS from "../constants";
import ModuleTranslations from "./ModuleTranslations";
import { getTranslatedDefinition } from "../utils";

const formTypeHelperText = ({ formValues }) => {
  switch (formValues.type) {
    case CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.RESERVATION_CONFIRMATION:
      return "Se envía cuando se confirma la reserva. Solo puede estar activa una plantilla al mismo tiempo.";
    case CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.RESERVATION_STATUS_RED:
      return "Se envía cuando el pago de la reserva ha fallado.";
    case CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.POST_STAY:
      return "Se envía días después de la estancia del huésped.";
    case CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.PRE_STAY:
      return "Se envía días antes de la llegada del huésped.";
    case CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.CART_ABANDONMENT:
      return "Se enviará el email si el huésped había iniciado sesión como usuario loyalty y tenía al menos una reserva en el carrito.";
    case CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.PURCHASE_EVENT:
    case CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.GIFT_CARD:
    default:
      return "";
  }
};

const isCartAbandonmentType = type =>
  type === CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.CART_ABANDONMENT;
const isReservationFailedType = type =>
  type === CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.RESERVATION_STATUS_RED;
const isReservationAbandonedPaymentType = type =>
  type === CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.RESERVATION_STATUS_ORANGE;
const isPostStayType = type => type === CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.POST_STAY;
const isPreStayType = type => type === CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.PRE_STAY;
const isReservationConfirmationType = type =>
  type === CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.RESERVATION_CONFIRMATION;

const ModuleDefinition = {
  name: "Email Templates",
  entityName: CONSTANTS.ENTITY.EMAIL_TEMPLATE.name,
  restConfig: {
    baseURL: `${BASE_URL}`,
    create: {
      url: () => `/api/emails/templates/`,
      method: "post",
      resetCache: true,
    },
    update: {
      url: () => `/api/emails/templates/`,
      method: "put",
      resetCache: true,
    },
    fetch: {
      url: ({ hotelTicker, id }) => `/api/emails/${hotelTicker}/templates/${id ? id : ""}`,
      method: "get",
    },
    delete: {
      url: ({ hotelTicker, id }) => `/api/emails/${hotelTicker}/templates/${id}`,
      method: "delete",
      resetCache: true,
    },
  },
  entities: [],
  renderers: {
    list: {},
    form: {},
  }, //render functions by attribute name, this overrides default widgets specified in the fields section
  translation: {
    translated: true,
    fieldName: "translations",
    displayName: "translations",
    widget: "translations",
    entityProperty: "translations",
  },
  fields: {
    list: [
      //columns to be used in the table/list display, Order IS important
      {
        fieldName: "active",
        displayName: "is Active",
        widget: "singleSwitch",
        toggleAction: true,
      },
      {
        fieldName: "type",
        displayName: "Template type",
      },
      {
        fieldName: "emailDestinationType",
        displayName: "emailDestinationType",
      },
    ],
    form: [
      //fields to be used in the creation/edit form display, Order IS important
      {
        fieldName: "id",
        widget: "hidden",
      },
      {
        fieldName: "hotelTicker",
        displayName: "hotel Ticker",
        widget: "hidden",
      },
      {
        fieldName: "isActive",
        displayName: "isActive",
        widget: "checkbox",
        defaultValue: false,
      },
      {
        fieldName: "type",
        displayName: "Email Type",
        widget: "selector",
        optionDisplay: {
          RESERVATION_CONFIRMATION: {
            label: "Reservation Confirmation",
          },
          RESERVATION_STATUS_RED: {
            label: "Failed Payment",
          },
          GIFT_CARD: {
            label: "Gift Card",
          },
          PURCHASE_EVENT: {
            label: "Purchase Event",
          },
          POST_STAY: {
            label: "Post-stay",
          },
          PRE_STAY: {
            label: "Pre-stay",
          },
          CART_ABANDONMENT: {
            label: "Cart abandonment",
          },
        },
        transform: ({ field, userInfo, formValues }) => {
          const isWitAdmin = userInfo?.isWitAdmin;
          const extraOptions = isWitAdmin
            ? {
                RESERVATION_STATUS_ORANGE: {
                  label: "Proceso de pago abandonado",
                },
              }
            : {};
          return {
            ...field,
            optionDisplay: { ...field.optionDisplay, ...extraOptions },
            className: "negativeMargin",
            helpText: formTypeHelperText({ formValues }),
          };
        },
        defaultValue: CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.RESERVATION_CONFIRMATION,
      },
      {
        fieldName: "delay",
        displayName: "Delay",
        widget: "selector",
        optionDisplay: {
          5: {
            label: "5 minutes",
          },
          10: {
            label: "10 minutes",
          },
          15: {
            label: "15 minutes",
          },
          30: {
            label: "30 minutes",
          },
          45: {
            label: "45 minutes",
          },
          60: {
            label: "60 minutes",
          },
        },
        helpText: "Amount of minutes before an email is sent.",
        placeholder: "Enter Delay",
        visible: ({ formValues }) =>
          isReservationFailedType(formValues.type) ||
          isReservationAbandonedPaymentType(formValues.type),
        defaultValue: 5,
      },
      {
        fieldName: "delayCartAbandonment",
        displayName: "Delay",
        widget: "selector",
        optionDisplay: {
          60: {
            label: "1 hour",
          },
          120: {
            label: "2 hours",
          },
          180: {
            label: "3 hours",
          },
          360: {
            label: "6 hours",
          },
          720: {
            label: "12 hours",
          },
          1440: {
            label: "24 hours",
          },
          2880: {
            label: "48 hours",
          },
        },
        helpText: "Amount of hours before an email is sent.",
        placeholder: "Enter Delay",
        visible: ({ formValues }) => isCartAbandonmentType(formValues.type),
        defaultValue: 60,
      },
      {
        fieldName: "emailDestinationType",
        displayName: "Email Destination Type",
        widget: "selector",
        optionDisplay: {
          ALL: {
            label: "All",
          },
          GUEST: {
            label: "Guest",
          },
          PROPERTY: {
            label: "Property",
          },
        },
        defaultValue: CONSTANTS.ENTITY.EMAIL_TEMPLATE.DESTINATION_TYPES.ALL,
      },
      {
        fieldName: "emailGuestType",
        displayName: "Email Guest Type",
        widget: "hidden",
        optionDisplay: {
          ALL: {
            label: "All",
          },
          LOYALTY_GUEST: {
            label: "Loyalty guest",
          },
          NON_LOYALTY_GUEST: {
            label: "Non loyalty guest",
          },
        },
        defaultValue: CONSTANTS.ENTITY.EMAIL_TEMPLATE.GUEST_TYPES.LOYALTY_GUEST,
        visible: ({ formValues }) => isCartAbandonmentType(formValues.type),
      },
      {
        fieldName: "delayCheckout",
        displayName: "Días posteriores al check-out para el envío",
        widget: "input",
        className: "wbk-daysInput",
        visible: ({ formValues }) => isPostStayType(formValues.type),
      },
      {
        fieldName: "delayCheckin",
        displayName: "Días previos al Check-in para el envío",
        widget: "input",
        className: "wbk-daysInput",
        visible: ({ formValues }) => isPreStayType(formValues.type),
      },
      {
        fieldName: "chronoUnit",
        displayName: "Delay units",
        widget: "hidden",
        optionDisplay: {
          MINUTES: {
            label: "Minutes",
          },
          HOURS: {
            label: "Hours",
          },
          DAYS: {
            label: "Days",
          },
        },
        defaultValue: "MINUTES",
        visible: ({ formValues }) => !isReservationConfirmationType(formValues.type),
      },
      {
        fieldName: "templateType",
        displayName: "Template type",
        widget: "selector",
        optionDisplay: {
          DEFAULT: {
            label: "Default template",
          },
          CUSTOM: {
            label: "Custom template",
          },
        },
        defaultValue: "DEFAULT",
        visible: ({ formValues }) => isReservationConfirmationType(formValues.type),
      },
      {
        fieldName: "singleMailOnMultipleRoomStays",
        description:
          "Para reservas múltiples agrupar confirmaciones en un solo email (Solo visible para WIT)",
        widget: "checkbox",
        defaultValue: false,
        visible: ({ formValues, userInfo }) =>
          userInfo.isWitAdmin && isReservationConfirmationType(formValues.type),
      },
      {
        fieldName: "subject",
        displayName: "subject",
        widget: "input",
        translated: true,
        defaultValue: {
          en:
            "{{#isModified}}Update: {{/isModified}}{{#reservationStatus.isPending}}Booking (Unconfirmed) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPending}}{{#reservationStatus.isCanceled}}The reservation has been cancelled. {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isCanceled}}{{#reservationStatus.isConfirmed}}{{#reservationStatus.isPaymentConfirmed}}Booking (confirmed) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentConfirmed}}{{#reservationStatus.isPaymentFailed}}Booking (confirmed) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentFailed}}{{#reservationStatus.isPaymentPending}}Booking (Unconfirmed) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentPending}}{{#reservationStatus.isPaymentExpired}}Booking (Unconfirmed) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentExpired}}{{/reservationStatus.isConfirmed}}",
          ca:
            "{{#isModified}}Modificació: {{/isModified}}{{#reservationStatus.isPending}}Reserva (sense confirmar) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPending}}{{#reservationStatus.isCanceled}}Reserva cancel·lada correctament. {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isCanceled}}{{#reservationStatus.isConfirmed}}{{#reservationStatus.isPaymentConfirmed}}Reserva (confirmada) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentConfirmed}}{{#reservationStatus.isPaymentFailed}}Reserva (confirmada) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentFailed}}{{#reservationStatus.isPaymentPending}}Reserva (sense confirmar) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentPending}}{{#reservationStatus.isPaymentExpired}}Reserva (sense confirmar) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentExpired}}{{/reservationStatus.isConfirmed}}",
          fr:
            "{{#isModified}}Modification: {{/isModified}}{{#reservationStatus.isPending}}Réservation (non confirmée) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPending}}{{#reservationStatus.isCanceled}}La réservation a été annulée. {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isCanceled}}{{#reservationStatus.isConfirmed}}{{#reservationStatus.isPaymentConfirmed}}Réservation (confirmée) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentConfirmed}}{{#reservationStatus.isPaymentFailed}}Réservation (confirmée) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentFailed}}{{#reservationStatus.isPaymentPending}}Réservation (non confirmée) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentPending}}{{#reservationStatus.isPaymentExpired}}Réservation (non confirmée) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentExpired}}{{/reservationStatus.isConfirmed}}",
          eu:
            "{{#isModified}}Modificación: {{/isModified}}{{#reservationStatus.isPending}}Erreserba (ez da baieztatu) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPending}}{{#reservationStatus.isCanceled}}Errerserba zuzenki ezeztatua {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isCanceled}}{{#reservationStatus.isConfirmed}}{{#reservationStatus.isPaymentConfirmed}}Erreserba (berretsita) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentConfirmed}}{{#reservationStatus.isPaymentFailed}}Erreserba (berretsita) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentFailed}}{{#reservationStatus.isPaymentPending}}Erreserba (ez da baieztatu) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentPending}}{{#reservationStatus.isPaymentExpired}}Erreserba (ez da baieztatu) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentExpired}}{{/reservationStatus.isConfirmed}}",
          es:
            "{{#isModified}}Modificación: {{/isModified}}{{#reservationStatus.isPending}}Reserva (Sin confirmar) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPending}}{{#reservationStatus.isCanceled}}Reserva cancelada correctamente {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isCanceled}}{{#reservationStatus.isConfirmed}}{{#reservationStatus.isPaymentConfirmed}}Reserva (confirmada) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentConfirmed}}{{#reservationStatus.isPaymentFailed}}Reserva (confirmada) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentFailed}}{{#reservationStatus.isPaymentPending}}Reserva (Sin confirmar) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentPending}}{{#reservationStatus.isPaymentExpired}}Reserva (Sin confirmar) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentExpired}}{{/reservationStatus.isConfirmed}}",
          de:
            "{{#isModified}}Modifikation: {{/isModified}}{{#reservationStatus.isPending}}Buchung (unbestätigt) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPending}}{{#reservationStatus.isCanceled}}Die Buchung wurde richtig storniert {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isCanceled}}{{#reservationStatus.isConfirmed}}{{#reservationStatus.isPaymentConfirmed}}Buchung (bestätigt) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentConfirmed}}{{#reservationStatus.isPaymentFailed}}Buchung (bestätigt) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentFailed}}{{#reservationStatus.isPaymentPending}}Buchung (unbestätigt) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentPending}}{{#reservationStatus.isPaymentExpired}}Buchung (unbestätigt) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentExpired}}{{/reservationStatus.isConfirmed}}",
          it:
            "{{#isModified}}Cambiamento: {{/isModified}}{{#reservationStatus.isPending}}Prenotazione (non confermata) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPending}}{{#reservationStatus.isCanceled}}La prenotazione è stata cancellata. {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isCanceled}}{{#reservationStatus.isConfirmed}}{{#reservationStatus.isPaymentConfirmed}}Prenotazione (confermata) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentConfirmed}}{{#reservationStatus.isPaymentFailed}}Prenotazione (confermata) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentFailed}}{{#reservationStatus.isPaymentPending}}Prenotazione (non confermata) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentPending}}{{#reservationStatus.isPaymentExpired}}Prenotazione (non confermata) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentExpired}}{{/reservationStatus.isConfirmed}}",
          pt:
            "{{#isModified}}Modificação: {{/isModified}}{{#reservationStatus.isPending}}Reserva (não confirmado) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPending}}{{#reservationStatus.isCanceled}}A reserva foi cancelada. {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isCanceled}}{{#reservationStatus.isConfirmed}}{{#reservationStatus.isPaymentConfirmed}}Reserva (confirmada) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentConfirmed}}{{#reservationStatus.isPaymentFailed}}Reserva (confirmada) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentFailed}}{{#reservationStatus.isPaymentPending}}Reserva (não confirmado) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentPending}}{{#reservationStatus.isPaymentExpired}}Reserva (não confirmado) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentExpired}}{{/reservationStatus.isConfirmed}}",
          ru:
            "{{#isModified}}модификация: {{/isModified}}{{#reservationStatus.isPending}}Бронирование (Не подтверждено) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPending}}{{#reservationStatus.isCanceled}}Бронирование было отменено. {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isCanceled}}{{#reservationStatus.isConfirmed}}{{#reservationStatus.isPaymentConfirmed}}Бронирование (подтверждено) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentConfirmed}}{{#reservationStatus.isPaymentFailed}}Бронирование (подтверждено) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentFailed}}{{#reservationStatus.isPaymentPending}}Бронирование (Не подтверждено) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentPending}}{{#reservationStatus.isPaymentExpired}}Бронирование (Не подтверждено) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentExpired}}{{/reservationStatus.isConfirmed}}",
          nl:
            "{{#isModified}}Wijziging: {{/isModified}}{{#reservationStatus.isPending}}Boeking (niet bevestigd) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPending}}{{#reservationStatus.isCanceled}}De reservering is geannuleerd. {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isCanceled}}{{#reservationStatus.isConfirmed}}{{#reservationStatus.isPaymentConfirmed}}Boeking (bevestigd) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentConfirmed}}{{#reservationStatus.isPaymentFailed}}Boeking (bevestigd) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentFailed}}{{#reservationStatus.isPaymentPending}}Boeking (niet bevestigd) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentPending}}{{#reservationStatus.isPaymentExpired}}Boeking (niet bevestigd) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentExpired}}{{/reservationStatus.isConfirmed}}",
          kr:
            "{{#isModified}}수정: {{/isModified}}{{#reservationStatus.isPending}}예약 (미확인) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPending}}{{#reservationStatus.isCanceled}}이 예약은 취소되었습니다 {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isCanceled}}{{#reservationStatus.isConfirmed}}{{#reservationStatus.isPaymentConfirmed}}예약 (확인) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentConfirmed}}{{#reservationStatus.isPaymentFailed}}예약 (확인) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentFailed}}{{#reservationStatus.isPaymentPending}}예약 (미확인) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentPending}}{{#reservationStatus.isPaymentExpired}}예약 (미확인) {{subjectReservationId}}, {{{property.name}}}{{/reservationStatus.isPaymentExpired}}{{/reservationStatus.isConfirmed}}",
        },
        visible: ({ formValues, userInfo }) =>
          (userInfo.isWitAdmin || !isReservationConfirmationType(formValues.type)) &&
          (formValues.templateType === CONSTANTS.ENTITY.EMAIL_TEMPLATE.TEMPLATE_TYPES.CUSTOM ||
            !isReservationConfirmationType(formValues.type)),
      },
      {
        fieldName: "body",
        displayName: "body",
        translated: true,
        visible: ({ formValues }) =>
          formValues.templateType === CONSTANTS.ENTITY.EMAIL_TEMPLATE.TEMPLATE_TYPES.CUSTOM ||
          !isReservationConfirmationType(formValues.type),
        transform: ({ field, formValues }) => {
          return {
            ...field,
            fullPage: true,
            widget:
              isPreStayType(formValues.type) || isPostStayType(formValues.type)
                ? "richHTMLEditor"
                : "textarea",
          };
        },
      },
      {
        fieldName: "header",
        displayName: "Header",
        widget: "richHTMLEditor",
        translated: true,
        defaultValue: {
          en: "{nombre_cliente}",
          ca: "{nombre_cliente}",
          fr: "{nombre_cliente}",
          eu: "{nombre_cliente}",
          es: "{nombre_cliente}",
          de: "{nombre_cliente}",
          it: "{nombre_cliente}",
          pt: "{nombre_cliente}",
          ru: "{nombre_cliente}",
          nl: "{nombre_cliente}",
          kr: "{nombre_cliente}",
        },
        visible: ({ formValues }) =>
          formValues.templateType === CONSTANTS.ENTITY.EMAIL_TEMPLATE.TEMPLATE_TYPES.DEFAULT &&
          isReservationConfirmationType(formValues.type),
      },
      {
        fieldName: "footer",
        displayName: "Footer",
        widget: "richHTMLEditor",
        translated: true,
        visible: ({ formValues }) =>
          formValues.templateType === CONSTANTS.ENTITY.EMAIL_TEMPLATE.TEMPLATE_TYPES.DEFAULT &&
          isReservationConfirmationType(formValues.type),
      },
    ],
  },
  validation: {
    id: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: true,
        },
      },
    },
    hotelTicker: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    type: {
      type: "Enum",
      constraints: {
        inclusion: {
          within: [
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.BACKEND_ERROR,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.RESERVATION_FAILURE,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.RESERVATION_STATUS_RED,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.RESERVATION_STATUS_ORANGE,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.RESERVATION_CONFIRMATION,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.GIFT_CARD,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.PURCHASE_EVENT,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.PRE_STAY,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.POST_STAY,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.CART_ABANDONMENT,
          ],
        },
        presence: {
          allowEmpty: false,
        },
      },
      defaultValue: CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.RESERVATION_CONFIRMATION,
    },
    delay: {
      type: "Number",
      constraints: {
        numericality: {
          greaterThanOrEqualTo: 0,
        },
        presence: {
          allowEmpty: false,
        },
      },
      defaultValue: 0,
    },
    delayCartAbandonment: {
      type: "Number",
      constraints: {
        numericality: {
          greaterThanOrEqualTo: 0,
        },
        presence: {
          allowEmpty: false,
        },
      },
      defaultValue: 60,
    },
    delayCheckout: {
      type: "Number",
      constraints: {
        numericality: {
          greaterThanOrEqualTo: 0,
        },
        presence: {
          allowEmpty: false,
        },
      },
    },
    delayCheckin: {
      type: "Number",
      constraints: {
        numericality: {
          greaterThanOrEqualTo: 0,
        },
        presence: {
          allowEmpty: false,
        },
      },
    },
    chronoUnit: {
      type: "Enum",
      constraints: {
        inclusion: {
          within: [
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.CHRONO_UNITS.SECONDS,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.CHRONO_UNITS.MINUTES,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.CHRONO_UNITS.HOURS,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.CHRONO_UNITS.DAYS,
          ],
        },
        presence: {
          allowEmpty: false,
        },
      },
      defaultValue: CONSTANTS.ENTITY.EMAIL_TEMPLATE.CHRONO_UNITS.MINUTES,
    },
    emailDestinationType: {
      type: "Enum",
      constraints: {
        inclusion: {
          within: [
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.DESTINATION_TYPES.PROPERTY,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.DESTINATION_TYPES.GUEST,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.DESTINATION_TYPES.ALL,
          ],
        },
        presence: {
          allowEmpty: false,
        },
      },
      defaultValue: CONSTANTS.ENTITY.EMAIL_TEMPLATE.DESTINATION_TYPES.ALL,
    },
    emailGuestType: {
      type: "Enum",
      constraints: {
        inclusion: {
          within: [
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.GUEST_TYPES.LOYALTY_GUEST,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.GUEST_TYPES.NON_LOYALTY_GUEST,
            CONSTANTS.ENTITY.EMAIL_TEMPLATE.GUEST_TYPES.ALL,
          ],
        },
        presence: {
          allowEmpty: false,
        },
      },
      defaultValue: CONSTANTS.ENTITY.EMAIL_TEMPLATE.GUEST_TYPES.ALL,
    },
    active: {
      type: "Boolean",
      constraints: {
        presence: {
          allowEmpty: true,
        },
      },
    },
    singleMailOnMultipleRoomStays: {
      type: "Boolean",
      constraint: {
        presence: {
          allowEmpty: true,
        },
      },
    },
    subject: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    body: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    header: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
    footer: {
      type: "String",
      constraints: {
        presence: {
          allowEmpty: false,
        },
      },
    },
  },
};

export default ({ locale }) =>
  getTranslatedDefinition({ ModuleDefinition, ModuleTranslations, locale });
